// Catalogue store.
//
// Holds the version manifest plus a small cache of per-product
// catalogues fetched from the read-path Worker. The Dashboard reads
// from this store on mount; the Catalogue view reads the per-product
// cubes; the History view reads historical cubes by hash via the
// read-path Worker's ?hash= query.
//
// The store does not persist between sessions — every page load
// re-fetches the manifest. KV-backed reads are cheap enough that the
// freshness is worth the round-trip.

import { api, ApiError } from './api';
import type {
  CatalogueResponse,
  Jurisdiction,
  ManifestResponse,
  ManifestVersionEntry,
  ProductCatalogueFile,
  ProductId,
  VersionManifest
} from './types';

interface CatalogueCacheEntry {
  catalogue: ProductCatalogueFile;
  meta: CatalogueResponse['meta'];
  fetched_at: number;
}

// $state-tracked record (not Map — Svelte 5 runes track property
// mutations on plain objects, not Map.set / Map.delete).
const state = $state<{
  loading: boolean;
  manifest: VersionManifest | null;
  manifestError: string | null;
  catalogues: Record<string, CatalogueCacheEntry>;
  catalogueErrors: Record<string, string>;
  // Timestamp of the most recent successful bootstrapManifest. The
  // Header surfaces this as "Refreshed X ago" so the operator sees
  // catalogue staleness at a glance and can hit the Refresh affordance
  // when in doubt.
  lastRefreshedAt: number | null;
  // True while a manual or auto refresh is mid-flight. The Refresh
  // button disables itself so rapid clicks do not stack fetches.
  refreshing: boolean;
}>({
  loading: true,
  manifest: null,
  manifestError: null,
  catalogues: {},
  catalogueErrors: {},
  lastRefreshedAt: null,
  refreshing: false
});

export const catalogueState = state;

function productKey(jurisdiction: Jurisdiction, product: ProductId, hash?: string): string {
  return hash ? `${jurisdiction}/${product}@${hash}` : `${jurisdiction}/${product}`;
}

export async function bootstrapManifest(): Promise<void> {
  state.loading = true;
  state.manifestError = null;
  try {
    const response: ManifestResponse = await api.manifest();
    state.manifest = response.manifest;
    state.lastRefreshedAt = Date.now();
    state.loading = false;
  } catch (err) {
    state.loading = false;
    state.manifestError = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'manifest_load_failed';
  }
}

export async function fetchCatalogue(jurisdiction: Jurisdiction, product: ProductId, hash?: string): Promise<ProductCatalogueFile | null> {
  const key = productKey(jurisdiction, product, hash);
  // Hashed reads address an immutable historical cube — cache freely;
  // returning the cached entry is safe forever. Unhashed reads address
  // the current in_effect cube which can change at any time when a
  // proposal merges, and there's no client-side signal to invalidate.
  // Always re-fetch the unhashed path; KV reads through the read-path
  // Worker are cheap (<100 ms) and freshness is worth the round-trip,
  // same trade-off bootstrapManifest already makes.
  if (hash && state.catalogues[key]) {
    return state.catalogues[key].catalogue;
  }
  try {
    const response = await api.catalogue(jurisdiction, product, hash);
    const entry: CatalogueCacheEntry = {
      catalogue: response.catalogue,
      meta: response.meta,
      fetched_at: Date.now()
    };
    state.catalogues[key] = entry;
    delete state.catalogueErrors[key];
    return entry.catalogue;
  } catch (err) {
    const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'catalogue_load_failed';
    state.catalogueErrors[key] = message;
    return null;
  }
}

export function getInEffectVersion(
  manifest: VersionManifest | null,
  jurisdiction: Jurisdiction,
  product: ProductId
): ManifestVersionEntry | null {
  if (!manifest) return null;
  const versions = manifest.jurisdictions?.[jurisdiction]?.[product]?.versions || [];
  return versions.find((v) => v.status === 'in_effect') ?? null;
}

export function getQueuedVersions(
  manifest: VersionManifest | null,
  jurisdiction: Jurisdiction,
  product: ProductId
): ManifestVersionEntry[] {
  if (!manifest) return [];
  const versions = manifest.jurisdictions?.[jurisdiction]?.[product]?.versions || [];
  return versions.filter((v) => v.status === 'approved' || v.status === 'under_review');
}

export function getAllVersions(
  manifest: VersionManifest | null,
  jurisdiction: Jurisdiction,
  product: ProductId
): ManifestVersionEntry[] {
  if (!manifest) return [];
  return manifest.jurisdictions?.[jurisdiction]?.[product]?.versions || [];
}

export function cachedCatalogue(jurisdiction: Jurisdiction, product: ProductId, hash?: string): ProductCatalogueFile | null {
  return state.catalogues[productKey(jurisdiction, product, hash)]?.catalogue ?? null;
}

// Focus-aware live-catalogue refresh.
//
// PR #51 made the catalogue store re-fetch on mount, which clears stale
// data when the user navigates between views. But a user who STAYS on
// a view (e.g. Dashboard) while a proposal merges in another tab keeps
// the cached value until they navigate away and back. The deploy
// pipeline + KV write completes inside ~2 minutes; the data is right,
// the SPA is just not polling for it.
//
// Visibility-aware refresh closes that gap at zero idle cost. When the
// tab becomes visible after being hidden — the user's natural pattern
// of switching to GitHub to merge a PR, then switching back — the store
// re-fetches the manifest. For each cached UNHASHED catalogue, if the
// in-effect git_hash has changed, that catalogue is re-fetched.
//
// Hashed reads (HistoryView point-in-time queries) are immutable and
// stay cached forever; the refresh ignores them.

let listenerInstalled = false;
let lastRefreshAt = 0;
const MIN_REFRESH_INTERVAL_MS = 5_000;

export function installLiveCatalogueRefresh(): void {
  if (typeof document === 'undefined') return;
  if (listenerInstalled) return;
  listenerInstalled = true;
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

async function handleVisibilityChange(): Promise<void> {
  if (typeof document === 'undefined') return;
  if (document.visibilityState !== 'visible') return;
  await refreshLiveCatalogues();
}

async function refreshLiveCatalogues(): Promise<void> {
  // Throttle so two rapid focus events do not double-fetch.
  const now = Date.now();
  if (now - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) return;
  lastRefreshAt = now;
  // Skip if the manifest fetch itself is in flight — bootstrapManifest
  // sets state.loading true while running.
  if (state.loading) return;

  state.refreshing = true;
  try {
    const previousManifest = state.manifest;
    await bootstrapManifest();
    if (!previousManifest || !state.manifest) return;

    // Walk every cached UNHASHED catalogue and re-fetch when its
    // in-effect git_hash has shifted on the new manifest. Hashed reads
    // (keys with '@') address an immutable historical cube and stay
    // cached.
    const refetchPromises: Promise<unknown>[] = [];
    for (const key of Object.keys(state.catalogues)) {
      if (key.includes('@')) continue;
      const slash = key.indexOf('/');
      if (slash < 0) continue;
      const jurisdiction = key.slice(0, slash) as Jurisdiction;
      const product = key.slice(slash + 1) as ProductId;
      const prev = getInEffectVersion(previousManifest, jurisdiction, product);
      const next = getInEffectVersion(state.manifest, jurisdiction, product);
      if (prev?.git_hash !== next?.git_hash) {
        refetchPromises.push(fetchCatalogue(jurisdiction, product));
      }
    }
    await Promise.all(refetchPromises);
  } finally {
    state.refreshing = false;
  }
}

// Manual-refresh entrypoint backing the Header's Refresh button. Unlike
// refreshLiveCatalogues (the visibilitychange handler) this:
//   - bypasses the 5-second throttle (the click IS the explicit signal)
//   - force-refetches every cached UNHASHED catalogue regardless of
//     whether git_hash shifted — the operator clicked Refresh because
//     they want fresh data, not because the manifest decided they do.
export async function refreshLiveCataloguesNow(): Promise<void> {
  if (state.refreshing) return;
  state.refreshing = true;
  try {
    await bootstrapManifest();
    if (!state.manifest) return;
    const refetchPromises: Promise<unknown>[] = [];
    for (const key of Object.keys(state.catalogues)) {
      if (key.includes('@')) continue;
      const slash = key.indexOf('/');
      if (slash < 0) continue;
      const jurisdiction = key.slice(0, slash) as Jurisdiction;
      const product = key.slice(slash + 1) as ProductId;
      refetchPromises.push(fetchCatalogue(jurisdiction, product));
    }
    await Promise.all(refetchPromises);
    // Bump the timestamp once the catalogue refetch has settled so the
    // chip reads "just now" when the user looks back.
    lastRefreshAt = Date.now();
  } finally {
    state.refreshing = false;
  }
}
